import type { QueryKey } from "@tanstack/react-query";
import { queryKeys } from "./queryKeys";

/**
 * Domain entities that own cached server state.
 */
export const InvalidationEntity = {
  ACTIVITY: "activity",
  COMMENTS: "comments",
  INVESTMENT: "investment",
  BENEFICIARY: "beneficiary",
  BENEFICIARY_DOCUMENT: "beneficiaryDocument",
  INVESTOR: "investor",
  PROJECT: "project",
  FISCAL_QUOTA: "fiscalQuota",
  CONTRACT: "contract",
  CLASSIFICATION: "classification",
  TEMPLATE: "template",
  PROPOSAL: "proposal",
  ORGANIZATION: "organization",
  NOTIFICATION: "notification",
  NOTE: "note",
  TAG: "tag",
  USER: "user",
  HUB: "hub",
  ADMIN: "admin",
  DASHBOARD: "dashboard",
  PIPELINE: "pipeline",
  TASK: "task",
} as const;

export type InvalidationEntity = (typeof InvalidationEntity)[keyof typeof InvalidationEntity];

/**
 * The kinds of cache an entity owns. A mutation reconciles its own lists,
 * aggregates and money; details and documents only when it could not write them
 * from the server response. `money` names every key that carries the entity's
 * served money, wherever it is embedded.
 */
export type Facet = "lists" | "details" | "documents" | "aggregates" | "money";

export const ALL_FACETS: readonly Facet[] = [
  "lists",
  "details",
  "documents",
  "aggregates",
  "money",
];

type FacetKeys = Partial<Record<Facet, QueryKey[]>>;

const ENTITY_KEYS: Record<InvalidationEntity, (orgId: string) => FacetKeys> = {
  activity: (orgId) => ({
    lists: [queryKeys.activity.all(orgId), queryKeys.investmentEvents.all(orgId)],
  }),
  comments: (orgId) => ({
    lists: [queryKeys.comments.lists(orgId)],
    details: [queryKeys.comments.details(orgId)],
    aggregates: [queryKeys.comments.summaries(orgId)],
  }),
  investment: (orgId) => ({
    lists: [queryKeys.investments.lists(orgId), queryKeys.investments.kanbanColumns(orgId)],
    details: [queryKeys.investments.details(orgId)],
    documents: [queryKeys.investments.documentsAll(orgId)],
    aggregates: [queryKeys.investments.aggregatesAll(orgId)],
  }),
  beneficiary: (orgId) => ({
    lists: [queryKeys.beneficiaries.lists(orgId), queryKeys.beneficiaryDropdown.all(orgId)],
    details: [queryKeys.beneficiaries.details(orgId)],
    documents: [
      queryKeys.beneficiaries.documentsAll(orgId),
      queryKeys.beneficiaries.documentsByQuotaAll(orgId),
    ],
    aggregates: [
      queryKeys.beneficiaries.aggregatesAll(orgId),
      queryKeys.beneficiaries.metricsAll(orgId),
    ],
  }),
  beneficiaryDocument: (orgId) => ({
    lists: [
      queryKeys.beneficiaries.documentsAll(orgId),
      queryKeys.beneficiaries.documentsByQuotaAll(orgId),
    ],
  }),
  investor: (orgId) => ({
    lists: [queryKeys.investors.lists(orgId), queryKeys.investorDropdown.all(orgId)],
    details: [
      queryKeys.investors.records(orgId),
      queryKeys.investors.investmentsAll(orgId),
      queryKeys.investors.investmentMetricsAll(orgId),
    ],
    documents: [queryKeys.investors.documentsAll(orgId)],
    aggregates: [
      queryKeys.investors.aggregatesAll(orgId),
      queryKeys.investors.metricsAll(orgId),
      queryKeys.investors.dashboardMetricsAll(orgId),
    ],
    money: [
      queryKeys.investors.lists(orgId),
      queryKeys.investorDropdown.all(orgId),
      queryKeys.investors.aggregatesAll(orgId),
    ],
  }),
  fiscalQuota: (orgId) => ({
    lists: [
      queryKeys.beneficiaries.quotaLists(orgId),
      queryKeys.beneficiaries.fiscalQuotaLists(orgId),
    ],
    details: [
      queryKeys.beneficiaries.quotas(orgId),
      queryKeys.beneficiaries.consolidationPreviews(orgId),
      queryKeys.beneficiaries.quotaReportsAll(orgId),
    ],
    aggregates: [
      queryKeys.beneficiaries.quotaAggregatesAll(orgId),
      queryKeys.beneficiaries.fiscalYearSummaries(orgId),
    ],
    money: [
      queryKeys.beneficiaries.quotaLists(orgId),
      queryKeys.beneficiaries.fiscalQuotaLists(orgId),
      queryKeys.beneficiaries.quotas(orgId),
      queryKeys.beneficiaries.quotaAggregatesAll(orgId),
      queryKeys.beneficiaries.details(orgId),
    ],
  }),
  project: (orgId) => ({
    lists: [queryKeys.projects.beneficiaryProjectsAll(orgId)],
    details: [queryKeys.projects.details(orgId)],
  }),
  contract: (orgId) => ({
    lists: [queryKeys.contracts.lists(orgId)],
    details: [queryKeys.contracts.details(orgId)],
    aggregates: [queryKeys.contracts.aggregatesAll(orgId)],
  }),
  classification: (orgId) => ({
    lists: [queryKeys.classifications.lists(orgId)],
    details: [queryKeys.classifications.details(orgId)],
    aggregates: [
      queryKeys.classifications.summaries(orgId),
      queryKeys.classifications.analyticsAll(orgId),
      queryKeys.classifications.countsAll(orgId),
    ],
  }),
  template: (orgId) => ({
    lists: [
      queryKeys.templates.lists(orgId),
      queryKeys.templateDropdown.all(orgId),
      queryKeys.organization.templates(orgId),
    ],
    details: [queryKeys.templates.details(orgId)],
  }),
  proposal: (orgId) => ({
    lists: [queryKeys.proposals.lists(orgId)],
    details: [queryKeys.proposals.details(orgId), queryKeys.proposals.publicAll()],
  }),
  organization: (orgId) => ({
    lists: [queryKeys.organization.all(orgId), queryKeys.organization.userOrganizations()],
  }),
  notification: () => ({
    lists: [queryKeys.notifications.lists()],
    aggregates: [queryKeys.notifications.stats()],
  }),
  note: (orgId) => ({
    lists: [queryKeys.notes.lists(orgId)],
    details: [queryKeys.notes.details(orgId)],
    aggregates: [queryKeys.notes.aggregatesAll(orgId)],
  }),
  tag: (orgId) => ({ lists: [queryKeys.tags.all(orgId)] }),
  user: () => ({ details: [queryKeys.userProfile.all()] }),
  hub: () => ({ lists: [queryKeys.hub.all] }),
  admin: () => ({ lists: [queryKeys.admin.all()] }),
  dashboard: (orgId) => ({ aggregates: [queryKeys.dashboard.all(orgId)] }),
  pipeline: (orgId) => ({ aggregates: [queryKeys.pipeline.all(orgId)] }),
  task: (orgId) => ({
    lists: [queryKeys.tasks.lists(orgId)],
    aggregates: [queryKeys.tasks.aggregatesAll(orgId), queryKeys.tasks.summary(orgId)],
  }),
};

export type Dependent = InvalidationEntity | readonly [InvalidationEntity, readonly Facet[]];

/**
 * One-hop dependency graph: when an entity changes, which facets of which other
 * entities show data derived from it. Read as "a change to [key] makes these
 * stale". Edges are not followed transitively.
 */
const ENTITY_DEPENDENTS: Record<InvalidationEntity, readonly Dependent[]> = {
  investment: [
    ["fiscalQuota", ["lists", "details", "aggregates", "money"]],
    ["beneficiary", ["lists", "aggregates"]],
    ["investor", ["lists", "details", "aggregates", "money"]],
    ["contract", ["lists", "aggregates"]],
    ["proposal", ["lists", "details"]],
    ["task", ["lists", "aggregates"]],
    ["activity", ["lists"]],
    "dashboard",
    "pipeline",
  ],
  beneficiary: [
    ["investment", ["lists"]],
    ["fiscalQuota", ["lists"]],
    ["tag", ["lists"]],
    ["activity", ["lists"]],
    "dashboard",
    "pipeline",
  ],
  beneficiaryDocument: [
    ["investment", ["lists", "details"]],
    ["task", ["lists", "aggregates"]],
    ["activity", ["lists"]],
  ],
  investor: [
    ["investment", ["lists"]],
    ["task", ["lists", "aggregates"]],
    ["tag", ["lists"]],
    ["activity", ["lists"]],
    "dashboard",
  ],
  fiscalQuota: [
    ["beneficiary", ["lists", "details", "aggregates"]],
    ["project", ["lists"]],
    ["investment", ["lists"]],
    ["task", ["lists", "aggregates"]],
    ["activity", ["lists"]],
    "pipeline",
  ],
  project: [
    ["beneficiary", ["lists", "details"]],
    ["fiscalQuota", ["lists", "details"]],
    ["activity", ["lists"]],
    "dashboard",
  ],
  contract: [
    ["investment", ["lists", "details", "documents"]],
    ["task", ["lists", "aggregates"]],
    ["activity", ["lists"]],
    "dashboard",
  ],
  classification: [
    ["fiscalQuota", ["lists", "details", "aggregates"]],
    ["beneficiary", ["details"]],
    ["task", ["lists", "aggregates"]],
    ["activity", ["lists"]],
  ],
  template: [["activity", ["lists"]]],
  proposal: [
    ["task", ["lists", "aggregates"]],
    ["activity", ["lists"]],
  ],
  note: [
    ["investment", ["details"]],
    ["activity", ["lists"]],
  ],
  tag: [
    ["investor", ["lists", "details"]],
    ["beneficiary", ["lists", "details"]],
  ],
  organization: [],
  comments: [],
  notification: [],
  user: [],
  hub: [],
  admin: [],
  activity: [],
  dashboard: [],
  pipeline: [],
  task: [],
};

/** Prefix keys of the given facets of one entity. */
export function entityKeys(
  entity: InvalidationEntity,
  orgId: string,
  facets: readonly Facet[] = ALL_FACETS,
): QueryKey[] {
  const keys = ENTITY_KEYS[entity](orgId);
  return facets.flatMap((facet) => keys[facet] ?? []);
}

/** Prefix keys of every facet that depends on the entity, one hop away. */
export function dependentKeys(entity: InvalidationEntity, orgId: string): QueryKey[] {
  return ENTITY_DEPENDENTS[entity].flatMap((dependent) =>
    typeof dependent === "string"
      ? entityKeys(dependent, orgId)
      : entityKeys(dependent[0], orgId, dependent[1]),
  );
}

/**
 * Every facet of each entity plus its dependents. Used by effects that cannot
 * be narrowed further (creates without a seed, bulk results, external refreshes).
 */
export function resolveInvalidationKeys(
  entities: readonly InvalidationEntity[],
  orgId: string,
): QueryKey[] {
  return entities.flatMap((entity) => [
    ...entityKeys(entity, orgId),
    ...dependentKeys(entity, orgId),
  ]);
}
